import { useEffect, useState } from "react";
import { authApi, reportApi, messageFrom } from "../api";
import UsageBar from "./UsageBar";
import ReportViewer from "./ReportViewer";
import ApiKeyPanel from "./ApiKeyPanel";
import BuyLimits from "./BuyLimits";
import SubUsers from "./SubUsers";

function Dashboard({ user, onLoggedOut }) {
    const [vin, setVin] = useState("");
    const [reportVin, setReportVin] = useState(""); // VIN of the currently displayed report
    const [html, setHtml] = useState("");
    const [loading, setLoading] = useState(false);
    const [cleaning, setCleaning] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [stats, setStats] = useState(null);
    const [tab, setTab] = useState("reports"); // reports | api | billing | subusers
    const isSubuser = user?.role === "subuser";

    const loadStats = () => {
        authApi
            .stats()
            .then((res) => setStats(res.data.data))
            .catch(() => {});
    };
    useEffect(loadStats, []);

    useEffect(() => {
        if (new URLSearchParams(window.location.search).get("payment")) setTab("billing");
    }, []);

    const cleanHtml = (raw, username) => {
        const container = document.createElement("div");
        container.innerHTML = raw;
        if (username !== "carexpress") {
            container.querySelector(".name-in-lights-snackbar")?.remove();
            container.querySelector(".report-provided-by-dealer-name")?.remove();
        }
        return container.innerHTML;
    };

    const getReport = () => {
        const clean = vin.replace(/\s/g, "").toUpperCase();
        setError("");
        setNotice("");
        if (clean.length !== 17) {
            setError("VIN must be 17 characters long");
            return;
        }
        setLoading(true);
        reportApi
            .getByVin(clean)
            .then((res) => {
                setHtml(cleanHtml(res.data.html, res.data.username));
                setReportVin(clean);
                loadStats();
            })
            .catch((err) => setError(messageFrom(err, "Could not load report")))
            .finally(() => setLoading(false));
    };

    const cleanVin = () => {
        const clean = vin.replace(/\s/g, "").toUpperCase();
        if (clean.length !== 17) {
            setError("VIN must be 17 characters long");
            return;
        }
        setError("");
        setCleaning(true);
        reportApi
            .cleanVin(clean)
            .then((res) => setNotice(res.data.message || "Cleared"))
            .catch((err) => setError(messageFrom(err, "Could not clear VIN")))
            .finally(() => setCleaning(false));
    };

    const logout = () => {
        authApi.logout().finally(onLoggedOut);
    };

    // The PDF is built server-side (Puppeteer) from the same cached report;
    // the response is the finished file.
    const exportPdf = async () => {
        if (!reportVin) return;
        setError("");
        setExporting(true);
        try {
            const res = await reportApi.exportPdf(reportVin);
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `carfax-${reportVin}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            let message = "Could not export PDF";
            try {
                const body = err.response && err.response.data;
                if (body instanceof Blob) {
                    message = JSON.parse(await body.text()).message || message;
                }
            } catch (_) {}
            setError(message);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="dash">
            <header className="dash__nav">
                <div className="brand">
                    <span className="brand__mark">CF</span>
                    <span className="brand__name">Carfax Reports</span>
                </div>
                <div className="dash__nav-right">
                    <span className="dash__user">{user?.name || user?.username}</span>
                    <button className="btn btn--ghost" onClick={logout}>Log out</button>
                </div>
            </header>

            <div className="dash__body">
                <aside className="dash__side">
                    <UsageBar stats={stats} />
                    <nav className="sidenav">
                        <button className={`sidenav__item${tab === "reports" ? " is-active" : ""}`} onClick={() => setTab("reports")}>Reports</button>
                        {!isSubuser && (
                            <button className={`sidenav__item${tab === "billing" ? " is-active" : ""}`} onClick={() => setTab("billing")}>Buy reports</button>
                        )}
                        {!isSubuser && (
                            <button className={`sidenav__item${tab === "subusers" ? " is-active" : ""}`} onClick={() => setTab("subusers")}>Sub-users</button>
                        )}
                    </nav>
                </aside>

                <main className="dash__main">
                    {tab === "api" && <ApiKeyPanel stats={stats} onChanged={loadStats} />}
                    {tab === "billing" && !isSubuser && <BuyLimits onPurchased={loadStats} />}
                    {tab === "subusers" && !isSubuser && <SubUsers />}

                    {tab === "reports" && <>
                    <div className="search">
                        <input
                            className="search__input"
                            placeholder="Enter 17-character VIN"
                            value={vin}
                            maxLength={20}
                            onChange={(e) => setVin(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !loading) getReport();
                            }}
                        />
                        <button className="btn btn--primary" onClick={getReport} disabled={loading}>
                            {loading ? "Checking…" : "Check"}
                        </button>
                        <button className="btn btn--ghost" onClick={cleanVin} disabled={cleaning}>
                            {cleaning ? "Clearing…" : "Clean VIN"}
                        </button>
                        <button className="btn btn--ghost" onClick={exportPdf} disabled={exporting || !html}>
                            {exporting ? "Exporting…" : "Download PDF"}
                        </button>
                    </div>

                    {error && <div className="banner banner--error">{error}</div>}
                    {notice && <div className="banner banner--ok">{notice}</div>}

                    <ReportViewer html={html} />
                    </>}
                </main>
            </div>
        </div>
    );
}

export default Dashboard;
